import { properties, payments, maintenance, documents, reminders, expenses } from '../api';
import { createStatCard } from '../components/card';
import { createDataTable, type Column } from '../components/table';
import { confirmModal, showModal } from '../components/modal';
import { showToast } from '../components/toast';
import { navigate } from '../router';
import type { Property, Payment, MaintenanceIssue, BlobDocument, PaymentSummary, Reminder, PropertyExpense } from '../types';

declare const Chart: any;

function fmt(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(n);
}

export async function propertyDetailPage(params: Record<string, string>): Promise<HTMLElement> {
  const id = params.id;
  const page = document.createElement('div');

  let prop: Property;
  try {
    prop = await properties.get(id);
  } catch {
    page.innerHTML = '<div class="text-red-500 p-8">Property not found.</div>';
    return page;
  }

  // Header
  const header = document.createElement('div');
  header.className = 'page-header';
  header.innerHTML = `
    <div>
      <a href="#/properties" class="text-sm text-gray-400 hover:text-primary-600 mb-1 inline-block">&larr; All Properties</a>
      <h1 class="text-2xl font-bold text-gray-900">${prop.address}</h1>
      <p class="text-gray-500">${prop.suburb}, ${prop.state} ${prop.postcode}</p>
    </div>
    <div class="flex gap-2">
      <a href="#/properties/${id}/edit" class="btn-outline">Edit</a>
      <button class="btn-danger" id="delete-prop-btn">Delete</button>
    </div>
  `;
  page.appendChild(header);

  // Delete handler
  setTimeout(() => {
    page.querySelector('#delete-prop-btn')?.addEventListener('click', async () => {
      if (await confirmModal('Delete this property and all its data? This cannot be undone.')) {
        try {
          await properties.delete(id);
          showToast('Property deleted', 'success');
          navigate('#/properties');
        } catch { showToast('Failed to delete', 'error'); }
      }
    });
  });

  // Tabs
  const tabs = ['Overview', 'Payments', 'Maintenance', 'Expenses', 'Documents', 'Reminders'];
  const tabBar = document.createElement('div');
  tabBar.className = 'flex border-b border-gray-200 mb-6';
  tabs.forEach((t, i) => {
    const btn = document.createElement('button');
    btn.className = `tab ${i === 0 ? 'active' : ''}`;
    btn.textContent = t;
    btn.setAttribute('data-tab', t.toLowerCase());
    tabBar.appendChild(btn);
  });
  page.appendChild(tabBar);

  const content = document.createElement('div');
  page.appendChild(content);

  const showTab = async (name: string) => {
    tabBar.querySelectorAll('.tab').forEach(t => t.classList.toggle('active', t.getAttribute('data-tab') === name));
    content.innerHTML = '<div class="text-center py-8 text-gray-400">Loading...</div>';
    try {
      switch (name) {
        case 'overview': await renderOverview(content, prop, id); break;
        case 'payments': await renderPayments(content, id); break;
        case 'maintenance': await renderMaintenance(content, id); break;
        case 'expenses': await renderExpenses(content, id); break;
        case 'documents': await renderDocuments(content, id); break;
        case 'reminders': await renderReminders(content, id); break;
      }
    } catch {
      content.innerHTML = '<div class="text-red-500 p-4">Failed to load tab data.</div>';
    }
  };

  tabBar.addEventListener('click', (e) => {
    const tab = (e.target as HTMLElement).getAttribute('data-tab');
    if (tab) showTab(tab);
  });

  showTab('overview');
  return page;
}

async function renderOverview(container: HTMLElement, p: Property, propertyId: string): Promise<void> {
  // Fetch summary data in parallel
  const [paymentSummary, expenseList, reminderList, maintenanceList] = await Promise.all([
    payments.summary(propertyId).catch(() => null),
    expenses.list(propertyId).catch(() => []),
    reminders.forProperty(propertyId).catch(() => []),
    maintenance.list(propertyId).catch(() => []),
  ]);

  const daysLeft = p.lease_end_date ? Math.ceil((new Date(p.lease_end_date).getTime() - Date.now()) / 86400000) : null;

  // Active reminders & open maintenance
  const activeReminders = reminderList.filter(r => r.status !== 'acknowledged');
  const openMaint = maintenanceList.filter(m => m.status === 'open' || m.status === 'in-progress');

  // Expense totals
  const totalAnnualExp = expenseList.reduce((s, e) => s + e.annual_amount, 0);

  container.innerHTML = '';

  // Main grid with sidebar
  const wrapper = document.createElement('div');
  wrapper.className = 'grid grid-cols-1 lg:grid-cols-3 gap-6';

  // Left column (2 cols wide) — property details + financials
  const mainCol = document.createElement('div');
  mainCol.className = 'lg:col-span-2 space-y-6';

  // Financial summary cards
  if (paymentSummary) {
    const finRow = document.createElement('div');
    finRow.className = 'grid grid-cols-2 md:grid-cols-4 gap-4';
    finRow.appendChild(createStatCard('Rent YTD', fmt(paymentSummary.total_ytd)));
    finRow.appendChild(createStatCard('Expected YTD', fmt(paymentSummary.expected_ytd)));
    finRow.appendChild(createStatCard('Agent Fees YTD', fmt(paymentSummary.agent_fees_ytd), undefined, 'text-red-500'));
    finRow.appendChild(createStatCard('Expenses/yr', fmt(totalAnnualExp), `${fmt(totalAnnualExp / 12)}/mo`, 'text-orange-500'));
    mainCol.appendChild(finRow);
  }

  mainCol.innerHTML += `
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Property Details</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Estimated Value</dt><dd class="font-medium">${p.estimated_value ? fmt(p.estimated_value) : '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Weekly Rent</dt><dd class="font-medium">${fmt(p.weekly_rent)}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Tenant</dt><dd class="font-medium">${p.tenant_name || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Lease Period</dt><dd class="font-medium">${p.lease_start_date || '—'} to ${p.lease_end_date || '—'}</dd></div>
          ${daysLeft !== null ? `<div class="flex justify-between"><dt class="text-gray-500">Lease Remaining</dt><dd class="font-medium ${daysLeft < 80 ? 'text-orange-500' : ''}">${daysLeft} days</dd></div>` : ''}
        </dl>
      </div>
      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Agency &amp; Agent</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Agency</dt><dd class="font-medium">${p.agency_name || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Contact</dt><dd class="font-medium">${p.agency_contact || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Agent</dt><dd class="font-medium">${p.agent_name || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Agent Phone</dt><dd class="font-medium">${p.agent_phone || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Mgmt Fee</dt><dd class="font-medium">${p.management_fee_percent || 0}%</dd></div>
        </dl>
      </div>
      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Insurance</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Company</dt><dd class="font-medium">${p.insurance_company || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Policy #</dt><dd class="font-medium">${p.insurance_policy_number || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Annual Cost</dt><dd class="font-medium">${fmt(p.insurance_annual_cost || 0)}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Renewal Date</dt><dd class="font-medium">${p.insurance_renewal_date || '—'}</dd></div>
        </dl>
      </div>
      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Compliance</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Gas Safety</dt><dd class="font-medium">${p.gas_safety_expiry || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Electrical Safety</dt><dd class="font-medium">${p.electrical_safety_expiry || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Smoke Alarm</dt><dd class="font-medium">${p.smoke_alarm_expiry || '—'}</dd></div>
        </dl>
      </div>
    </div>
    ${p.notes ? `<div class="card"><h3 class="font-semibold text-gray-900 mb-2">Notes</h3><p class="text-sm text-gray-600 whitespace-pre-wrap">${p.notes}</p></div>` : ''}
  `;

  // Right sidebar — alerts & reminders
  const sidebar = document.createElement('div');
  sidebar.className = 'space-y-4';

  // Open maintenance
  if (openMaint.length > 0) {
    const maintCard = document.createElement('div');
    maintCard.className = 'card border-l-4 border-yellow-400';
    maintCard.innerHTML = `<h3 class="font-semibold text-gray-900 mb-3 text-sm">Open Maintenance (${openMaint.length})</h3>
      <div class="space-y-2">${openMaint.map(m => `
        <a href="#/properties/${propertyId}/maintenance/${m.id}" class="block p-2 rounded bg-gray-50 hover:bg-gray-100 text-sm">
          <div class="font-medium text-gray-800 truncate">${m.title || m.description || 'Issue'}</div>
          <div class="flex gap-2 mt-1">
            <span class="badge priority-${m.priority} text-xs">${m.priority}</span>
            <span class="text-xs text-gray-400">${m.date_reported}</span>
          </div>
        </a>`).join('')}
      </div>`;
    sidebar.appendChild(maintCard);
  }

  // Active reminders
  if (activeReminders.length > 0) {
    const remCard = document.createElement('div');
    remCard.className = 'card border-l-4 border-blue-400';
    remCard.innerHTML = `<h3 class="font-semibold text-gray-900 mb-3 text-sm">Active Reminders (${activeReminders.length})</h3>
      <div class="space-y-2">${activeReminders.map(r => `
        <div class="p-2 rounded bg-gray-50 text-sm">
          <div class="font-medium text-gray-800">${r.type}</div>
          <div class="text-xs text-gray-500">Due: ${r.due_date}</div>
          <span class="badge badge-${r.status === 'sent' ? 'blue' : 'yellow'} text-xs">${r.status}</span>
        </div>`).join('')}
      </div>`;
    sidebar.appendChild(remCard);
  }

  // Compliance alerts
  const complianceAlerts: string[] = [];
  const today = Date.now();
  const warn60 = 60 * 86400000;
  const checks = [
    { label: 'Insurance Renewal', date: p.insurance_renewal_date },
    { label: 'Gas Safety', date: p.gas_safety_expiry },
    { label: 'Electrical Safety', date: p.electrical_safety_expiry },
    { label: 'Smoke Alarm', date: p.smoke_alarm_expiry },
  ];
  for (const c of checks) {
    if (!c.date) continue;
    const diff = new Date(c.date).getTime() - today;
    if (diff < 0) complianceAlerts.push(`<div class="text-red-600 text-sm font-medium">⚠ ${c.label} <span class="font-normal">expired (${c.date})</span></div>`);
    else if (diff < warn60) complianceAlerts.push(`<div class="text-yellow-600 text-sm font-medium">⚡ ${c.label} <span class="font-normal">in ${Math.ceil(diff/86400000)}d (${c.date})</span></div>`);
  }
  if (complianceAlerts.length > 0) {
    const compCard = document.createElement('div');
    compCard.className = 'card border-l-4 border-red-400';
    compCard.innerHTML = `<h3 class="font-semibold text-gray-900 mb-3 text-sm">Compliance Alerts</h3><div class="space-y-2">${complianceAlerts.join('')}</div>`;
    sidebar.appendChild(compCard);
  }

  if (sidebar.children.length === 0) {
    sidebar.innerHTML = '<div class="card text-center text-gray-400 text-sm py-6">No active alerts</div>';
  }

  wrapper.appendChild(mainCol);
  wrapper.appendChild(sidebar);
  container.appendChild(wrapper);
}

async function renderPayments(container: HTMLElement, propertyId: string): Promise<void> {
  const [paymentList, summary] = await Promise.all([
    payments.list(propertyId),
    payments.summary(propertyId),
  ]);

  container.innerHTML = '';

  // Summary stats
  const stats = document.createElement('div');
  stats.className = 'grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6';
  stats.appendChild(createStatCard('Total YTD', fmt(summary.total_ytd), `from ${summary.period_start}`));
  stats.appendChild(createStatCard('Expected YTD', fmt(summary.expected_ytd)));
  stats.appendChild(createStatCard('Agent Fees YTD', fmt(summary.agent_fees_ytd), undefined, 'text-red-500'));
  stats.appendChild(createStatCard('Payments', String(summary.payment_count)));
  container.appendChild(stats);

  // Payment Timeline
  try {
    const allTimelines = await payments.timeline(12);
    const propTimeline = allTimelines.find(t => t.property_id === propertyId);
    if (propTimeline && propTimeline.months.length > 0) {
      const timelineCard = document.createElement('div');
      timelineCard.className = 'card mb-6';
      timelineCard.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">Monthly Payment Timeline <span class="text-xs text-gray-400 font-normal">Expected vs Received</span></h3>`;

      const months = propTimeline.months;

      const canvasWrapper = document.createElement('div');
      canvasWrapper.style.position = 'relative';
      canvasWrapper.style.height = '200px';
      const canvas = document.createElement('canvas');
      canvasWrapper.appendChild(canvas);

      setTimeout(() => {
        new Chart(canvas, {
          type: 'bar',
          data: {
            labels: months.map(m => m.month.substring(5)),
            datasets: [
              {
                label: 'Expected',
                data: months.map(m => m.expected),
                backgroundColor: 'rgba(209, 213, 219, 0.8)',
                borderRadius: 2,
              },
              {
                label: 'Received',
                data: months.map(m => m.actual),
                backgroundColor: months.map(m =>
                  m.color === 'green' ? 'rgba(34, 197, 94, 0.7)' :
                  m.color === 'amber' ? 'rgba(251, 191, 36, 0.7)' :
                  m.color === 'red' ? 'rgba(239, 68, 68, 0.7)' :
                  'rgba(156, 163, 175, 0.5)'
                ),
                borderRadius: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { intersect: false, mode: 'index' },
            plugins: {
              tooltip: {
                position: 'nearest',
                callbacks: {
                  label: (ctx: any) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { callback: (v: number) => fmt(v) },
              },
            },
            onClick: (_evt: any, elements: any[]) => {
              if (elements.length === 0) return;
              const clickedMonth = months[elements[0].index]?.month;
              if (!clickedMonth) return;
              const rows = container.querySelectorAll('.payment-row');
              let first: HTMLElement | null = null;
              rows.forEach(r => {
                const row = r as HTMLElement;
                const match = (row.dataset.month || '') === clickedMonth;
                row.classList.toggle('bg-blue-50', match);
                if (match && !first) first = row;
              });
              if (first) (first as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            },
          },
        });
      }, 0);

      const chartDiv = document.createElement('div');
      chartDiv.appendChild(canvasWrapper);

      // Detailed table under chart
      const timelineTableDiv = document.createElement('div');
      timelineTableDiv.className = 'mt-4 overflow-x-auto';
      timelineTableDiv.innerHTML = `
        <table class="data-table text-sm">
          <thead><tr>
            <th>Month</th>
            <th class="text-right">Expected</th>
            <th class="text-right">Received</th>
            <th class="text-right">Variance</th>
            <th>Status</th>
          </tr></thead>
          <tbody>
            ${months.map(m => {
              const variance = m.actual - m.expected;
              const colorClass = m.color === 'green' ? 'badge-green' : m.color === 'amber' ? 'badge-yellow' : m.color === 'red' ? 'badge-red' : 'badge-gray';
              const statusLabel = m.color === 'green' ? 'On Track' : m.color === 'amber' ? 'Partial' : m.color === 'red' ? 'Missed' : 'No Data';
              return `<tr>
                <td class="font-medium">${m.month}</td>
                <td class="text-right">${fmt(m.expected)}</td>
                <td class="text-right font-medium">${m.actual ? fmt(m.actual) : '—'}</td>
                <td class="text-right ${variance < 0 ? 'text-red-600' : variance > 0 ? 'text-green-600' : ''}">${m.actual ? (variance >= 0 ? '+' : '') + fmt(variance) : '—'}</td>
                <td><span class="badge ${colorClass}">${statusLabel}</span></td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      `;
      timelineCard.appendChild(chartDiv);
      timelineCard.appendChild(timelineTableDiv);
      container.appendChild(timelineCard);
    }
  } catch {
    // Timeline is optional – don't block the payments view
  }

  // Add button
  const addBtn = document.createElement('button');
  addBtn.className = 'btn-primary mb-4';
  addBtn.textContent = '+ Add Payment';
  addBtn.addEventListener('click', () => showAddPaymentModal(propertyId, container));
  container.appendChild(addBtn);

  const cols: Column<Payment>[] = [
    { label: 'Date', key: 'payment_date' },
    { label: 'Amount', render: r => fmt(r.amount), className: 'text-right' },
    { label: 'Type', render: r => `<span class="badge badge-${r.type === 'rent' ? 'green' : 'blue'}">${r.type}</span>` },
    { label: 'Source', render: r => `<span class="badge badge-gray">${r.source}</span>` },
    { label: 'Reference', key: 'reference' },
    { label: 'Week Ending', key: 'week_ending' },
  ];
  const payTable = createDataTable(cols, paymentList, { emptyText: 'No payments recorded.' });
  // Tag rows with month for chart click → scroll/highlight
  const tbody = payTable.querySelector('tbody');
  if (tbody) {
    tbody.querySelectorAll('tr').forEach((tr, i) => {
      if (paymentList[i]) {
        tr.classList.add('payment-row');
        tr.dataset.month = paymentList[i].payment_date?.substring(0, 7) || '';
      }
    });
  }
  container.appendChild(payTable);
}

function showAddPaymentModal(propertyId: string, container: HTMLElement): void {
  const form = document.createElement('form');
  form.className = 'space-y-4';
  form.innerHTML = `
    <div><label class="label">Date</label><input name="payment_date" type="date" class="input-field" required value="${new Date().toISOString().split('T')[0]}"></div>
    <div><label class="label">Amount ($)</label><input name="amount" type="number" step="0.01" class="input-field" required></div>
    <div><label class="label">Type</label><select name="type" class="input-field"><option value="rent">Rent</option><option value="other">Other</option></select></div>
    <div><label class="label">Week Ending</label><input name="week_ending" type="date" class="input-field"></div>
    <div><label class="label">Reference</label><input name="reference" type="text" class="input-field"></div>
    <div><label class="label">Notes</label><textarea name="notes" class="input-field" rows="2"></textarea></div>
    <div class="flex justify-end gap-3"><button type="button" class="btn-outline cancel-btn">Cancel</button><button type="submit" class="btn-primary">Save</button></div>
  `;

  const { close } = showModal('Add Payment', form);
  form.querySelector('.cancel-btn')!.addEventListener('click', close);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    try {
      await payments.create(propertyId, {
        payment_date: fd.get('payment_date') as string,
        amount: parseFloat(fd.get('amount') as string),
        type: fd.get('type') as 'rent' | 'other',
        week_ending: fd.get('week_ending') as string,
        reference: fd.get('reference') as string,
        notes: fd.get('notes') as string,
        source: 'manual',
      });
      close();
      showToast('Payment added', 'success');
      await renderPayments(container, propertyId);
    } catch { showToast('Failed to add payment', 'error'); }
  });
}

async function renderMaintenance(container: HTMLElement, propertyId: string): Promise<void> {
  const issues = await maintenance.list(propertyId);
  container.innerHTML = '';

  const addBtn = document.createElement('button');
  addBtn.className = 'btn-primary mb-4';
  addBtn.textContent = '+ Report Issue';
  addBtn.addEventListener('click', () => {
    navigate(`#/properties/${propertyId}/maintenance/new`);
  });
  container.appendChild(addBtn);

  const cols: Column<MaintenanceIssue>[] = [
    { label: 'Title', render: r => r.title || r.description, className: 'max-w-xs truncate' },
    { label: 'Priority', render: r => `<span class="badge priority-${r.priority}">${r.priority}</span>` },
    { label: 'Status', render: r => `<span class="badge badge-${r.status === 'completed' ? 'green' : r.status === 'in-progress' ? 'yellow' : 'gray'}">${r.status}</span>` },
    { label: 'Reported', key: 'date_reported' },
    { label: 'Quoted', render: r => r.quoted_amount ? fmt(r.quoted_amount) : '—', className: 'text-right' },
    { label: 'Actual', render: r => r.actual_amount ? fmt(r.actual_amount) : '—', className: 'text-right' },
  ];
  container.appendChild(createDataTable(cols, issues, {
    emptyText: 'No maintenance issues.',
    onRowClick: (row) => navigate(`#/properties/${propertyId}/maintenance/${row.id}`),
  }));
}

async function renderDocuments(container: HTMLElement, propertyId: string): Promise<void> {
  const docs = await documents.list(propertyId);
  container.innerHTML = '';

  // Upload form
  const uploadArea = document.createElement('div');
  uploadArea.className = 'mb-6 p-4 border-2 border-dashed border-gray-200 rounded-lg';
  uploadArea.innerHTML = `
    <form id="upload-form" class="flex items-end gap-4 flex-wrap">
      <div>
        <label class="label">Category</label>
        <select name="category" class="input-field">
          <option value="lease">Lease</option>
          <option value="management">Management</option>
          <option value="insurance">Insurance</option>
          <option value="compliance">Compliance</option>
          <option value="maintenance">Maintenance</option>
          <option value="emails">Emails</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div class="flex-1">
        <label class="label">File</label>
        <input type="file" name="file" class="input-field" required>
      </div>
      <button type="submit" class="btn-primary">Upload</button>
    </form>
  `;
  container.appendChild(uploadArea);

  const uploadForm = uploadArea.querySelector('#upload-form') as HTMLFormElement;
  uploadForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(uploadForm);
    const file = fd.get('file') as File;
    const category = fd.get('category') as string;
    try {
      await documents.upload(propertyId, file, category);
      showToast('Document uploaded', 'success');
      await renderDocuments(container, propertyId);
    } catch { showToast('Upload failed', 'error'); }
  });

  // Documents list
  const cols: Column<BlobDocument>[] = [
    { label: 'Name', render: r => r.name.split('/').pop() || r.name },
    { label: 'Category', render: r => `<span class="badge badge-blue">${r.name.split('/')[0] || 'other'}</span>` },
    { label: 'Size', render: r => `${(r.size / 1024).toFixed(1)} KB` },
    { label: 'Modified', render: r => new Date(r.last_modified).toLocaleDateString() },
    {
      label: 'Actions',
      render: r => `<button class="text-primary-600 hover:underline text-sm dl-btn" data-name="${r.name}">Download</button>
        <button class="text-red-500 hover:underline text-sm ml-2 del-btn" data-name="${r.name}">Delete</button>`,
    },
  ];
  const table = createDataTable(cols, docs, { emptyText: 'No documents uploaded.' });
  container.appendChild(table);

  // Wire up action buttons
  table.querySelectorAll('.dl-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const name = btn.getAttribute('data-name')!;
      try {
        const { url } = await documents.download(propertyId, name);
        window.open(url, '_blank');
      } catch { showToast('Download failed', 'error'); }
    });
  });
  table.querySelectorAll('.del-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const name = btn.getAttribute('data-name')!;
      if (await confirmModal('Delete this document?')) {
        try {
          await documents.delete(propertyId, name);
          showToast('Deleted', 'success');
          await renderDocuments(container, propertyId);
        } catch { showToast('Delete failed', 'error'); }
      }
    });
  });
}

async function renderReminders(container: HTMLElement, propertyId: string): Promise<void> {
  const items = await reminders.forProperty(propertyId);
  container.innerHTML = '';

  const cols: Column<Reminder>[] = [
    { label: 'Type', key: 'type' },
    { label: 'Due Date', key: 'due_date' },
    { label: 'Status', render: r => `<span class="badge badge-${r.status === 'acknowledged' ? 'green' : r.status === 'sent' ? 'blue' : 'yellow'}">${r.status}</span>` },
    { label: 'Channel', render: r => `<span class="badge badge-gray">${r.channel}</span>` },
    {
      label: 'Actions',
      render: r => r.status !== 'acknowledged'
        ? `<button class="btn-ghost text-sm ack-btn" data-id="${r.id}">Acknowledge</button>`
        : '',
    },
  ];
  const table = createDataTable(cols, items, { emptyText: 'No reminders for this property.' });
  container.appendChild(table);

  table.querySelectorAll('.ack-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      try {
        await reminders.acknowledge(btn.getAttribute('data-id')!);
        showToast('Acknowledged', 'success');
        await renderReminders(container, propertyId);
      } catch { showToast('Failed', 'error'); }
    });
  });
}

async function renderExpenses(container: HTMLElement, propertyId: string): Promise<void> {
  const items = await expenses.list(propertyId);
  container.innerHTML = '';

  // Summary
  const totalAnnual = items.reduce((sum, e) => sum + e.annual_amount, 0);
  const stats = document.createElement('div');
  stats.className = 'grid grid-cols-2 gap-4 mb-6';
  stats.appendChild(createStatCard('Total Annual Expenses', fmt(totalAnnual)));
  stats.appendChild(createStatCard('Monthly Estimate', fmt(totalAnnual / 12)));
  container.appendChild(stats);

  // Add button
  const addBtn = document.createElement('button');
  addBtn.className = 'btn-primary mb-4';
  addBtn.textContent = '+ Add Expense';
  addBtn.addEventListener('click', () => showExpenseModal(propertyId, null, container));
  container.appendChild(addBtn);

  const cols: Column<PropertyExpense>[] = [
    { label: 'Type', render: r => `<span class="badge badge-blue">${r.type}</span>` },
    { label: 'Label', key: 'label' },
    { label: 'Annual', render: r => fmt(r.annual_amount), className: 'text-right' },
    { label: 'Period', key: 'period' },
    { label: 'Notes', key: 'notes', className: 'max-w-xs truncate' },
    {
      label: 'Actions',
      render: r => `
        <button class="text-primary-600 hover:underline text-sm edit-exp-btn" data-id="${r.id}">Edit</button>
        <button class="text-red-500 hover:underline text-sm ml-2 del-exp-btn" data-id="${r.id}">Delete</button>
      `,
    },
  ];
  const table = createDataTable(cols, items, { emptyText: 'No expenses recorded.' });
  container.appendChild(table);

  // Wire up action buttons
  table.querySelectorAll('.edit-exp-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const exp = items.find(x => x.id === btn.getAttribute('data-id'));
      if (exp) showExpenseModal(propertyId, exp, container);
    });
  });
  table.querySelectorAll('.del-exp-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (await confirmModal('Delete this expense?')) {
        try {
          await expenses.delete(propertyId, btn.getAttribute('data-id')!);
          showToast('Deleted', 'success');
          await renderExpenses(container, propertyId);
        } catch { showToast('Failed to delete', 'error'); }
      }
    });
  });
}

function showExpenseModal(propertyId: string, existing: PropertyExpense | null, container: HTMLElement): void {
  const form = document.createElement('form');
  form.className = 'space-y-4';
  form.innerHTML = `
    <div>
      <label class="label">Type</label>
      <select name="type" class="input-field">
        <option value="council_rates" ${existing?.type === 'council_rates' ? 'selected' : ''}>Council Rates</option>
        <option value="water_rates" ${existing?.type === 'water_rates' ? 'selected' : ''}>Water Rates</option>
        <option value="land_tax" ${existing?.type === 'land_tax' ? 'selected' : ''}>Land Tax</option>
        <option value="strata" ${existing?.type === 'strata' ? 'selected' : ''}>Strata / Body Corp</option>
        <option value="insurance" ${existing?.type === 'insurance' ? 'selected' : ''}>Insurance</option>
        <option value="other" ${existing?.type === 'other' ? 'selected' : ''}>Other</option>
      </select>
    </div>
    <div><label class="label">Label</label><input name="label" type="text" class="input-field" required value="${existing?.label || ''}" placeholder="e.g. Council rates Q1"></div>
    <div><label class="label">Annual Amount ($)</label><input name="annual_amount" type="number" step="0.01" class="input-field" required value="${existing?.annual_amount || ''}"></div>
    <div><label class="label">Period</label><input name="period" type="text" class="input-field" value="${existing?.period || ''}" placeholder="e.g. Quarterly, Annual"></div>
    <div><label class="label">Notes</label><textarea name="notes" class="input-field" rows="2">${existing?.notes || ''}</textarea></div>
    <div class="flex justify-end gap-3">
      <button type="button" class="btn-outline cancel-btn">Cancel</button>
      <button type="submit" class="btn-primary">${existing ? 'Update' : 'Add'}</button>
    </div>
  `;

  const { close } = showModal(existing ? 'Edit Expense' : 'Add Expense', form);
  form.querySelector('.cancel-btn')!.addEventListener('click', close);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const data = {
      type: fd.get('type') as string,
      label: fd.get('label') as string,
      annual_amount: parseFloat(fd.get('annual_amount') as string),
      period: fd.get('period') as string,
      notes: fd.get('notes') as string,
    };
    try {
      if (existing) {
        await expenses.update(propertyId, existing.id, data);
      } else {
        await expenses.create(propertyId, data);
      }
      close();
      showToast(existing ? 'Updated' : 'Added', 'success');
      await renderExpenses(container, propertyId);
    } catch { showToast('Failed to save', 'error'); }
  });
}
